/**
 * TempPasswordModal - Reusable modal to display a generated temporary password.
 *
 * Shows the password exactly once with a copy-to-clipboard button and a security
 * warning. Clears the password from state on close via the parent's onClose handler.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Check, Copy, AlertTriangle } from 'lucide-react';
import { Modal } from '../Modal';
import { Button } from '../Button';

// ============================================================================
// Types
// ============================================================================

export interface TempPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The generated temporary password. Pass null to prevent rendering. */
  temporaryPassword: string | null;
  /** Optional display label (name or email) shown beneath the title. */
  userLabel?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[5]};

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.status.warning.text};
    margin-top: 1px;
  }
`;

const WarningText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.warning.text};
  margin: 0;
  line-height: 1.5;
`;

const PasswordLabel = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
`;

const UserLabel = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing[4]};
`;

const PasswordBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const PasswordText = styled.span`
  flex: 1;
  font-family: monospace;
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-all;
  user-select: all;
`;

const CopyButton = styled.button<{ $copied: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid
    ${({ theme, $copied }) =>
      $copied ? theme.colors.status.success.border : theme.colors.primary.base};
  background: ${({ theme, $copied }) =>
    $copied ? theme.colors.status.success.bg : 'transparent'};
  color: ${({ theme, $copied }) =>
    $copied ? theme.colors.status.success.text : theme.colors.primary.base};
  cursor: pointer;
  flex-shrink: 0;
  transition: ${({ theme }) => theme.transitions.fast};

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: ${({ theme, $copied }) =>
      $copied ? theme.colors.status.success.bg : theme.colors.status.info.bg};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  margin-top: ${({ theme }) => theme.spacing[5]};
`;

// ============================================================================
// Component
// ============================================================================

export const TempPasswordModal = ({
  isOpen,
  onClose,
  temporaryPassword,
  userLabel,
}: TempPasswordModalProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset copied state whenever the modal opens with a new password
  useEffect(() => {
    if (isOpen) {
      setCopied(false);
    }
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, [isOpen]);

  const handleCopy = async () => {
    if (!temporaryPassword) return;

    try {
      await navigator.clipboard.writeText(temporaryPassword);
    } catch {
      // Fallback for environments without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = temporaryPassword;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }

    setCopied(true);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen || temporaryPassword === null) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('tempPassword.modalTitle')} size="sm">
      {userLabel && (
        <UserLabel>{t('tempPassword.userLabel', { name: userLabel })}</UserLabel>
      )}

      <WarningBanner role="alert">
        <AlertTriangle aria-hidden="true" />
        <WarningText>{t('tempPassword.warning')}</WarningText>
      </WarningBanner>

      <PasswordLabel>{t('tempPassword.passwordLabel')}</PasswordLabel>

      <PasswordBox>
        <PasswordText>{temporaryPassword}</PasswordText>
        <CopyButton
          $copied={copied}
          onClick={handleCopy}
          aria-label={t('tempPassword.ariaLabelCopy')}
        >
          {copied ? (
            <>
              <Check aria-hidden="true" />
              {t('tempPassword.copiedButton')}
            </>
          ) : (
            <>
              <Copy aria-hidden="true" />
              {t('tempPassword.copyButton')}
            </>
          )}
        </CopyButton>
      </PasswordBox>

      <ModalFooter>
        <Button variant="primary" onClick={onClose}>
          {t('tempPassword.doneButton')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TempPasswordModal;
